import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button, SectionTitle, Spinner } from "@porchpass/ui";
import type { DomainTaskStatusType } from "@porchpass/api-client";
import {
  getConditions,
  removeFromLetter,
  updateConditionStatus,
} from "src/api/preApproval";
import type {
  ConditionFilter,
  ConditionSection,
  PreApprovalCondition,
} from "src/api/preApproval/types";
import { PreApprovalEmptyState } from "../../components/PreApprovalEmptyState";
import { useWorkspace } from "../WorkspaceContext";
import { AddConditionForm } from "./AddConditionForm";
import { ConditionCard } from "./ConditionCard";
import { ConditionFilterChips } from "./ConditionFilterChips";
import { EditConditionModal } from "./EditConditionModal";

const SECTION_TITLES: Record<ConditionSection, string> = {
  customer: "Customer Conditions",
  funding: "Funding Conditions",
  seller: "Seller Conditions",
};

function matchesFilter(
  condition: PreApprovalCondition,
  filter: ConditionFilter,
): boolean {
  if (filter === "all") {
    return true;
  }
  return condition.status === filter;
}

interface ConditionsPanelProps {
  section: ConditionSection;
}

export function ConditionsPanel({ section }: ConditionsPanelProps) {
  const { applicationId } = useParams<{ applicationId: string }>();
  const { refreshCounts, conditionsRefreshKey } = useWorkspace();
  const [conditions, setConditions] = useState<PreApprovalCondition[]>([]);
  const [filter, setFilter] = useState<ConditionFilter>("all");
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingCondition, setEditingCondition] =
    useState<PreApprovalCondition | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadConditions = useCallback(async () => {
    if (!applicationId) {
      return;
    }
    setIsLoading(true);
    setLoadError(null);
    try {
      const nextConditions = await getConditions(applicationId, section);
      setConditions(nextConditions);
    } catch (error) {
      setConditions([]);
      setLoadError(
        error instanceof Error
          ? error.message
          : "Unable to load conditions right now.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [applicationId, section]);

  useEffect(() => {
    void loadConditions();
  }, [loadConditions, refreshKey, conditionsRefreshKey]);

  const filteredConditions = useMemo(
    () => conditions.filter((condition) => matchesFilter(condition, filter)),
    [conditions, filter],
  );

  const handleStatusChange = async (
    condition: PreApprovalCondition,
    status: DomainTaskStatusType,
  ) => {
    if (!applicationId) {
      return;
    }
    const updated = await updateConditionStatus(
      applicationId,
      condition,
      status,
    );
    setConditions((current) =>
      current.map((item) => (item.id === updated.id ? updated : item)),
    );
    await refreshCounts();
  };

  const handleRemoveFromLetter = async (condition: PreApprovalCondition) => {
    if (!applicationId) {
      return;
    }
    const updated = await removeFromLetter(applicationId, condition);
    setConditions((current) =>
      current.filter((item) => item.id !== updated.id),
    );
    await refreshCounts();
  };

  return (
    <div className="flex-1 flex flex-col gap-4 p-6 bg-[#FAFBFC] overflow-y-auto min-h-0">
      <SectionTitle
        size="md"
        title={SECTION_TITLES[section]}
        subtitle="Manage conditions included on the pre-approval letter."
        action={
          <Button
            size="s"
            onClick={() => setIsAddOpen(true)}
            disabled={!applicationId || Boolean(loadError)}
          >
            <Plus className="h-4 w-4" />
            Add condition
          </Button>
        }
      />

      <ConditionFilterChips value={filter} onChange={setFilter} />

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="s" />
        </div>
      ) : loadError ? (
        <PreApprovalEmptyState
          title="Conditions unavailable"
          description={loadError}
          actionLabel="Retry"
          onAction={() => setRefreshKey((value) => value + 1)}
        />
      ) : filteredConditions.length === 0 ? (
        <PreApprovalEmptyState
          title="No conditions"
          description={
            filter === "all"
              ? "There are no active conditions in this section yet."
              : "No conditions match the selected filter."
          }
        />
      ) : (
        <div className="flex flex-col gap-3">
          {filteredConditions.map((condition) => (
            <ConditionCard
              key={condition.id}
              condition={condition}
              onStatusChange={(status) => {
                void handleStatusChange(condition, status).catch(
                  () => undefined,
                );
              }}
              onEdit={() => setEditingCondition(condition)}
              onRemoveFromLetter={() => {
                void handleRemoveFromLetter(condition);
              }}
            />
          ))}
        </div>
      )}

      {editingCondition && (
        <EditConditionModal
          condition={editingCondition}
          onCancel={() => setEditingCondition(null)}
          onSuccess={(updated) => {
            setEditingCondition(null);
            setConditions((current) =>
              current.map((item) => (item.id === updated.id ? updated : item)),
            );
            void refreshCounts();
          }}
        />
      )}

      {isAddOpen && (
        <AddConditionForm
          section={section}
          onCancel={() => setIsAddOpen(false)}
          onSuccess={() => {
            setIsAddOpen(false);
            setRefreshKey((value) => value + 1);
            void refreshCounts();
          }}
        />
      )}
    </div>
  );
}
